import { useContext } from 'preact/hooks';
import { Fragment } from 'preact';
import PropTypes from 'prop-types';

import '../scss/components/_third-party-login.scss';
import {
  GoogleIcon, GoogleBlackIcon, FacebookIcon, FacebookBlackIcon, FacebookBlueIcon,
} from '../app/icons/social-icons';
import getSessionInfo from '../helpers/session-Info';
import StoreContext from '../helpers/context';
import {
  isINCHBrand,
  isAnswerforceBrand,
  isAnytimeBrand,
  isSignMoreBrand,
  isChatSupportBrand,
  isSetmoreStartNowPage,
} from '../helpers/brands';
import { PLANS_INITIAL_STATE_LITERAL } from '../utils/common-literals';
import { pushCustomEventToDataLayer } from '../helpers/analytics';
import { getUserSignupSource } from '../helpers/common-helper';

const {
  brand: { socialLogin: { facebook, google } },
  leadInfo: { authorized, authType },
} = getSessionInfo();

function getEncodedState(signupType) {
  return btoa(JSON.stringify({
    signupType,
    queryString: window.location.search,
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  }));
}

const HeaderText = () => {
  if (authorized) {
    return (
      <h4 className="heading-sm mb-6"
        data-testid="helper-text">
        Please complete the form
      </h4>
    );
  }
  if (isChatSupportBrand) {
    return (
      <h4 className="ta-center mb-6 awd-text--sm horizontal-line"
        data-testid="helper-text">
        or
      </h4>
    );
  }
  if (isAnswerforceBrand) {
    return (
      <h4 className="mb-6 fw-semibold sec-font heading-sm"
        data-testid="helper-text">
        Or register with email
      </h4>
    );
  }
  if (isAnytimeBrand) {
    return (
      <h4 className="mb-4 heading-sm"
        data-testid="helper-text">
        Or signup with email
      </h4>
    );
  }
  if (isINCHBrand) {
    return (
      <h4 className="mb-4 text"
        data-testid="helper-text">
        Or register with email
      </h4>
    );
  }
  return null;
};

export default function SocialLogin({ signupType }) {
  const { state } = useContext(StoreContext);

  const onSocialLogin = (event) => {
    const { type } = event.currentTarget.dataset;
    localStorage.setItem(PLANS_INITIAL_STATE_LITERAL, JSON.stringify(state.planDetails));
    if (isSetmoreStartNowPage()) {
      pushCustomEventToDataLayer({
        event: `signup-${type}${
          document.getElementsByClassName('is-through-email').length > 0
            ? '-icon'
            : ''
        }`,
        data: {
          source: getUserSignupSource(),
          timestamp: new Date().getTime(),
        },
      });
    }
    window.location.assign(`/auth/${type}?state=${getEncodedState(isSetmoreStartNowPage() ? 'setmoreStartNowPage' : signupType)}`);
  };
  const signedUpWithGoogle = authorized && authType === 'GOOGLE';
  const signedUpWithFb = authorized && authType === 'FACEBOOK';

  return (
    <Fragment>
      {isSetmoreStartNowPage() ? (
        <ul className='btn-holder' data-testid="startNowSocialLogin">
          {google && (
            <li>
              <button
                type="button"
                data-testid="google"
                data-click-id="google"
                data-type="google"
                className="btn btn-social fx-v"
                onClick={onSocialLogin}
                tabIndex="-1"
              >
                <GoogleIcon />
                <span className="btn-social-text text text-sm">Continue with Google</span>
              </button>
            </li>
          )}
          {facebook && (
            <li>
              <button
                data-testid="facebook"
                data-click-id="facebook"
                data-type="facebook"
                className="btn btn-social fx-v"
                onClick={onSocialLogin}
                tabIndex="-1"
              >
                <FacebookBlueIcon />
                <span className="btn-social-text text text-sm">Continue with Facebook</span>
              </button>
            </li>
          )}
        </ul>
      ) : (
        <>
          <div
            className={`third-party-login ${
              isINCHBrand || isAnytimeBrand ? 'mb-10' : 'mb-6'
            } ${authorized ? 'no-user-interaction' : ''}`}
          >
            {!isChatSupportBrand && (
              <h4
                className={`${isINCHBrand ? 'text' : 'heading-sm'} ${
                  isAnswerforceBrand ? 'fw-semibold sec-font' : ''
                } ${isINCHBrand ? 'mb-3' : 'mb-6'}`}
              >
                Use your social media account
              </h4>
            )}
            <ul className="btn-holder">
              {google && (
                <li>
                  <button
                    type="button"
                    data-testid="google"
                    data-type="google"
                    className={`${
                      isChatSupportBrand
                        ? 'awd-btn awd-btn-secondary'
                        : 'button-default'
                    } ${signedUpWithGoogle ? 'activated' : ''}`}
                    onClick={onSocialLogin}
                    tabIndex="-1"
                  >
                    {isINCHBrand ? <GoogleBlackIcon /> : <GoogleIcon />}
                    <span
                      className={`${
                        isChatSupportBrand
                          ? 'awd-btn-label awd-text--md fw-600'
                          : 'tc-grey text'
                      }`}
                    >
                      Sign{signedUpWithGoogle ? 'ed' : ''} up{' '}
                      <cite className="phone-hide lg-tab-hide">
                        with Google
                      </cite>
                    </span>
                  </button>
                </li>
              )}
              {facebook && (
                <li>
                  <button
                    type="button"
                    data-testid="facebook"
                    data-type="facebook"
                    className={`button-default button-fb ${
                      signedUpWithFb ? 'activated' : ''
                    }`}
                    onClick={onSocialLogin}
                    tabIndex="-1"
                  >
                    {isINCHBrand ? <FacebookBlackIcon /> : <FacebookIcon />}
                    <span className="text">
                      Sign{signedUpWithFb ? 'ed' : ''} up{' '}
                      <cite className="phone-hide lg-tab-hide">
                        with Facebook
                      </cite>
                    </span>
                  </button>
                </li>
              )}
            </ul>
          </div>
          <HeaderText />
          {isSignMoreBrand || isChatSupportBrand ? (
            <h4
              className={`${
                isChatSupportBrand
                  ? 'awd-heading--h3 fw-600 mb-4'
                  : 'heading-md fw-semiboldm mb-6'
              }`}
            >
              Create your account
            </h4>
          ) : (
            ''
          )}
        </>
      )}
    </Fragment>
  );
}

SocialLogin.propTypes = {
  signupType: PropTypes.string.isRequired,
};
